import { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, useState, useEffect } from "react";
import styled from "@emotion/styled";
import { MergeWithOverwrite } from "src/common";
import { errorA11yProps, errorIdFor } from "src/common/style/a11y.style";
import { colors } from "src/theme";
import { InputLabel } from "src/components";

const Input = styled.input((props: { hasError: boolean, disabled?: boolean }) => ({
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  width: '100%',
  height: '44px',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  fontFamily: "Red Hat Display",
  lineHeight: '24px',
  color: props.disabled ? colors.text3 : colors.text1,
  backgroundColor: props.disabled ? colors.background5 : 'transparent',
  borderRadius: '8px',
  border: `1px solid ${props.hasError ? colors.error1 : colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',

  '&[disabled]': {
    border: `1px solid ${colors.border5}`,
    color: colors.text2,
    pointerEvents: 'none' as const,
  },

  '&::-moz-placeholder': {
    color: colors.text4,
    opacity: 1,
  },

  '&:-ms-input-placeholder': {
    color: colors.text4,
  },

  '&::-webkit-input-placeholder': {
    color: colors.text4,
  },

  '&:focus::-moz-placeholder': {
    color: 'transparent',
    opacity: 1,
  },

  '&:focus:-ms-input-placeholder': {
    color: 'transparent',
  },

  '&:focus::-webkit-input-placeholder': {
    color: 'transparent',
  },

  '&:focus': {
    borderColor: colors.border4,
  },

  '&[type=number]::-webkit-inner-spin-button, &[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
}));

const ValidationError = styled.div({
  position: 'absolute',
  color: colors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
});

export type InputOIBProps = MergeWithOverwrite<React.InputHTMLAttributes<HTMLInputElement>, {
  label?: string;
  value?: string;
  error?: string;
  optional?: boolean;
  onChange?: (value: string) => void;
}
>;

export const InputOIB: React.FC<InputOIBProps> = ({
  label,
  error = '',
  value = "",
  onChange,
  optional = false,
  ...props
}) => {

  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {

    let input = e.target.value.replace(/[^\d]/g, '');

    if (input.length > 11) {
      input = input.slice(0, 11);
    }

    setInputValue(input);
    if (onChange) onChange(input);
  }

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
    // Spriječiti unos slova 'e', 'E', '+', '-', '.' i ','
    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.addEventListener("wheel", (e: WheelEvent) => {
      e.preventDefault();
    }, { passive: false });
  };


  return (
    <div>
      {label ? <InputLabel htmlFor={props.id} disabled={props.disabled} optional={optional}>{label}</InputLabel> : null}
      <Input
        autoComplete="off"
        {...props}
        type='number'
        id={props.id}
        {...errorA11yProps(props.id, error)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        hasError={!!error}
        disabled={props.disabled}
        value={inputValue}
        maxLength={11}
      />
      {error ? <ValidationError id={errorIdFor(props.id)}>{error}</ValidationError> : null}
    </div>
  );
};
